import React, { useState, useEffect } from 'react';
import { FiUpload, FiCheck, FiX, FiAlertCircle, FiClock, FiCamera, FiShield, FiExternalLink } from 'react-icons/fi';
import api from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';

const DriverDocuments = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [documents, setDocuments] = useState(null);
  const [identityStatus, setIdentityStatus] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    cnh_number: '',
    cnh_photo: '',
    vehicle_plate: '',
    vehicle_photo: '',
    crlv_photo: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [docsResponse, identityResponse] = await Promise.all([
        api.get('/driver/documents'),
        api.get('/driver/identity-status')
      ]);
      
      setDocuments(docsResponse.data);
      setIdentityStatus(identityResponse.data);
      
      if (docsResponse.data.cnh_number) {
        setFormData({
          cnh_number: docsResponse.data.cnh_number || '',
          cnh_photo: docsResponse.data.cnh_photo || '',
          vehicle_plate: docsResponse.data.vehicle_plate || '',
          vehicle_photo: docsResponse.data.vehicle_photo || '',
          crlv_photo: docsResponse.data.crlv_photo || ''
        });
      }
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartVerification = async () => {
    setVerifying(true);
    setError('');
    
    try {
      const response = await api.post('/driver/verify-identity');
      const { verification_url } = response.data;
      
      if (verification_url) {
        // Open Didit verification in new tab/window
        window.open(verification_url, '_blank');
        setSuccess('Página de verificação aberta! Complete o processo e volte aqui.');
        
        // Start polling for status updates
        const pollInterval = setInterval(async () => {
          try {
            const statusResponse = await api.get('/driver/identity-status');
            setIdentityStatus(statusResponse.data);
            
            if (statusResponse.data.identity_verified || statusResponse.data.identity_status === 'rejected') {
              clearInterval(pollInterval);
              loadData();
            }
          } catch (e) {
            console.error('Error polling status:', e);
          }
        }, 5000); // Poll every 5 seconds
        
        // Stop polling after 10 minutes
        setTimeout(() => clearInterval(pollInterval), 600000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao iniciar verificação');
    } finally {
      setVerifying(false);
    }
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError('Imagem muito grande. Máximo 5MB.');
      return;
    }

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await api.post('/upload/image', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setFormData(prev => ({ ...prev, [field]: response.data.url }));
      setError('');
    } catch (err) {
      setError('Erro ao fazer upload da imagem');
    }
  };

  const formatCNH = (value) => {
    return value.replace(/\D/g, '').slice(0, 11);
  };

  const formatPlate = (value) => {
    return value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 7);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (formData.cnh_number.length !== 11) {
      setError('CNH deve ter 11 dígitos');
      return;
    }
    
    if (formData.vehicle_plate.length < 7) {
      setError('Placa inválida');
      return;
    }
    
    if (!formData.cnh_photo) {
      setError('Envie a foto da CNH');
      return;
    }
    
    if (!formData.vehicle_photo) {
      setError('Envie a foto do veículo com a placa visível');
      return;
    }
    
    if (!formData.crlv_photo) {
      setError('Envie a foto do CRLV (documento do veículo) válido e ativo');
      return;
    }

    setSubmitting(true);
    
    try {
      await api.post('/driver/documents', formData);
      setSuccess('Documentos enviados! Agora faça a verificação de identidade.');
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao enviar documentos');
    } finally {
      setSubmitting(false);
    }
  };

  const getIdentityBadge = () => {
    if (!identityStatus) return null;
    
    if (identityStatus.identity_verified) {
      return (
        <div className="bg-green-100 text-green-800 px-4 py-3 rounded-xl flex items-center gap-3">
          <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
            <FiShield className="text-xl text-white" />
          </div>
          <div>
            <p className="font-semibold">✓ Identidade Verificada</p>
            <p className="text-sm">
              {identityStatus.verified_name && `Nome: ${identityStatus.verified_name}`}
            </p>
          </div>
        </div>
      );
    }
    
    if (identityStatus.identity_status === 'in_progress' || identityStatus.identity_status === 'pending') {
      return (
        <div className="bg-yellow-100 text-yellow-800 px-4 py-3 rounded-xl flex items-center gap-3">
          <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center">
            <FiClock className="text-xl text-white" />
          </div>
          <div>
            <p className="font-semibold">Verificação em Andamento</p>
            <p className="text-sm">Aguarde a conclusão da análise...</p>
          </div>
        </div>
      );
    }
    
    if (identityStatus.identity_status === 'rejected') {
      return (
        <div className="bg-red-100 text-red-800 px-4 py-3 rounded-xl flex items-center gap-3">
          <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
            <FiX className="text-xl text-white" />
          </div>
          <div>
            <p className="font-semibold">Verificação Rejeitada</p>
            <p className="text-sm">Tente novamente com documentos válidos.</p>
            <button
              onClick={handleStartVerification}
              disabled={verifying}
              className="mt-2 text-sm underline hover:no-underline"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      );
    }
    
    return null;
  };

  const getDocStatusBadge = () => {
    if (!documents) return null;
    
    switch (documents.documents_status) {
      case 'approved':
        return (
          <div className="bg-green-100 text-green-800 px-4 py-3 rounded-xl flex items-center gap-2">
            <FiCheck className="text-xl" />
            <div>
              <p className="font-semibold">Documentos Aprovados</p>
              <p className="text-sm">Você está habilitado para fazer entregas!</p>
            </div>
          </div>
        );
      case 'pending':
        return (
          <div className="bg-yellow-100 text-yellow-800 px-4 py-3 rounded-xl flex items-center gap-2">
            <FiClock className="text-xl" />
            <div>
              <p className="font-semibold">Documentos em Análise</p>
              <p className="text-sm">Seus documentos estão sendo verificados.</p>
            </div>
          </div>
        );
      case 'rejected':
        return (
          <div className="bg-red-100 text-red-800 px-4 py-3 rounded-xl flex items-center gap-2">
            <FiX className="text-xl" />
            <div>
              <p className="font-semibold">Documentos Rejeitados</p>
              <p className="text-sm">{documents.rejection_reason || 'Envie novamente.'}</p>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="w-8 h-8 border-4 border-[#DC143C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const isEditable = !documents?.documents_status || documents?.documents_status === 'rejected';
  const canVerifyIdentity = !identityStatus?.identity_verified && identityStatus?.identity_status !== 'in_progress';
  const needsIdentityVerification = !identityStatus?.identity_verified && documents?.documents_status !== 'not_submitted';

  return (
    <div className="space-y-6">
      {/* Identity Verification Card */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-[#DC143C] rounded-full flex items-center justify-center">
            <FiShield className="text-xl text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800">Verificação de Identidade</h2>
            <p className="text-sm text-gray-500">Validação biométrica com prova de vida</p>
          </div>
        </div>
        
        {getIdentityBadge()}
        
        {!identityStatus?.identity_verified && identityStatus?.identity_status !== 'in_progress' && (
          <div className="mt-4">
            <div className="bg-gray-50 rounded-xl p-4 mb-4">
              <h3 className="font-semibold text-gray-700 mb-2">Por que verificar?</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>✓ Maior confiança dos lojistas</li>
                <li>✓ Proteção contra fraudes</li>
                <li>✓ Aprovação instantânea</li>
                <li>✓ Prioridade nas entregas</li>
              </ul>
            </div>
            
            <button
              onClick={handleStartVerification}
              disabled={verifying}
              className="w-full py-4 bg-[#DC143C] text-white rounded-xl font-semibold hover:bg-[#B01030] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {verifying ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Iniciando...
                </>
              ) : (
                <>
                  <FiShield />
                  Verificar Minha Identidade
                  <FiExternalLink size={16} />
                </>
              )}
            </button>
            <p className="text-xs text-gray-500 text-center mt-2">
              Você será redirecionado para uma página segura
            </p>
          </div>
        )}
      </div>

      {/* Documents Card */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Documentos do Veículo</h2>
        
        {getDocStatusBadge()}
        
        <form onSubmit={handleSubmit} className="mt-6 space-y-6">
          {/* CNH */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-700 flex items-center gap-2">
              <span className="w-6 h-6 bg-[#DC143C] text-white rounded-full flex items-center justify-center text-sm">1</span>
              CNH - Carteira Nacional de Habilitação
            </h3>
            
            <div>
              <label className="block text-sm text-gray-600 mb-1">Número da CNH *</label>
              <input
                type="text"
                value={formData.cnh_number}
                onChange={(e) => setFormData(prev => ({ ...prev, cnh_number: formatCNH(e.target.value) }))}
                placeholder="00000000000"
                maxLength={11}
                disabled={!isEditable}
                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#DC143C] focus:border-[#DC143C] outline-none disabled:bg-gray-100"
              />
              <p className="text-xs text-gray-500 mt-1">11 dígitos, sem pontos ou traços</p>
            </div>
            
            <div>
              <label className="block text-sm text-gray-600 mb-1">Foto da CNH *</label>
              {formData.cnh_photo ? (
                <div className="relative inline-block">
                  <img src={formData.cnh_photo} alt="CNH" className="w-full max-w-xs rounded-xl border" />
                  {isEditable && (
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, cnh_photo: '' }))}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full"
                    >
                      <FiX />
                    </button>
                  )}
                </div>
              ) : (
                <label className="border-2 border-dashed border-gray-300 rounded-xl p-6 flex flex-col items-center gap-2 cursor-pointer hover:border-[#DC143C] transition-colors">
                  <FiCamera className="text-3xl text-gray-400" />
                  <span className="text-sm text-gray-500">Clique para enviar foto</span>
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => handleImageUpload(e, 'cnh_photo')}
                    disabled={!isEditable}
                    className="hidden"
                  />
                </label>
              )}
            </div>
          </div>

          {/* Veículo */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-700 flex items-center gap-2">
              <span className="w-6 h-6 bg-[#DC143C] text-white rounded-full flex items-center justify-center text-sm">2</span>
              Veículo
            </h3>
            
            <div>
              <label className="block text-sm text-gray-600 mb-1">Placa do Veículo *</label>
              <input
                type="text"
                value={formData.vehicle_plate}
                onChange={(e) => setFormData(prev => ({ ...prev, vehicle_plate: formatPlate(e.target.value) }))}
                placeholder="ABC1234 ou ABC1D23"
                maxLength={7}
                disabled={!isEditable}
                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#DC143C] focus:border-[#DC143C] outline-none disabled:bg-gray-100 uppercase"
              />
            </div>
            
            <div>
              <label className="block text-sm text-gray-600 mb-1">Foto do Veículo *</label>
              <p className="text-xs text-gray-400 mb-2">Foto da moto com a placa visível</p>
              {formData.vehicle_photo ? (
                <div className="relative inline-block">
                  <img src={formData.vehicle_photo} alt="Veículo" className="w-full max-w-xs rounded-xl border" />
                  {isEditable && (
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, vehicle_photo: '' }))}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full"
                    >
                      <FiX />
                    </button>
                  )}
                </div>
              ) : (
                <label className="border-2 border-dashed border-gray-300 rounded-xl p-6 flex flex-col items-center gap-2 cursor-pointer hover:border-[#DC143C] transition-colors">
                  <FiCamera className="text-3xl text-gray-400" />
                  <span className="text-sm text-gray-500">Clique para enviar foto</span>
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => handleImageUpload(e, 'vehicle_photo')}
                    disabled={!isEditable}
                    className="hidden"
                  />
                </label>
              )}
            </div>
            
            {/* CRLV - Documento do Veículo */}
            <div className="col-span-2 mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                📄 CRLV - Certificado de Registro e Licenciamento do Veículo *
              </label>
              <p className="text-xs text-yellow-700 mb-3">
                ⚠️ <strong>Obrigatório:</strong> O documento deve estar válido e ativo. Envie foto legível de frente e verso ou do documento digital.
              </p>
              {formData.crlv_photo ? (
                <div className="relative inline-block">
                  <img src={formData.crlv_photo} alt="CRLV" className="w-full max-w-md rounded-xl border-2 border-yellow-300" />
                  {isEditable && (
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, crlv_photo: '' }))}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full"
                    >
                      <FiX />
                    </button>
                  )}
                </div>
              ) : (
                <label className="border-2 border-dashed border-yellow-400 bg-white rounded-xl p-6 flex flex-col items-center gap-2 cursor-pointer hover:border-[#DC143C] transition-colors">
                  <FiCamera className="text-3xl text-yellow-500" />
                  <span className="text-sm text-gray-600 font-medium">Clique para enviar foto do CRLV</span>
                  <span className="text-xs text-gray-400">Documento do veículo válido e ativo</span>
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => handleImageUpload(e, 'crlv_photo')}
                    disabled={!isEditable}
                    className="hidden"
                  />
                </label>
              )}
            </div>
          </div>

          {/* Errors/Success */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
              {error}
            </div>
          )}
          
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl">
              {success}
            </div>
          )}

          {/* Submit */}
          {isEditable && (
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 bg-gray-800 text-white rounded-xl font-semibold hover:bg-gray-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <FiUpload />
                  Enviar Documentos
                </>
              )}
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

export default DriverDocuments;
